// Global Instructions Rule Applied!
//
// Relationships — Zod models for the Phase 0 LP-side foundations
// (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md): account owners, the LP
// Allocation grid, the relationship-facts ledger, LP fund exposure, and the
// two Configuration lookups. Keys follow the RPCs' table__column convention.
//
// Plain string ids (never .uuid()): system_users.id is an Entra object id and
// legacy Dataverse account ids are not all uuid-shaped. Every response is
// strict-validated before it reaches UI state; a failure is an error, never
// coerced (CLAUDE.md).
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// ── Account owners ─────────────────────────────────────────────────────────
// Phase 8 (person identity): ownership is a contact relationship — a
// contact_accounts row under an account_ownership title — so the owner is
// the durable contact; the login is derived from it and is NULL once the
// person has left.
export const AccountOwnerRecordSchema = z
  .object({
    contact_accounts__id: z.string(),
    contact_accounts__account_id: z.string(),
    contact_accounts__from_date: z.string().nullish(),
    contact_accounts__to_date: z.string().nullish(),
    contact_accounts__notes: z.string().nullish(),
    contact_accounts__date_modified: z.string().nullish(),
    contact_accounts__created_actor: z.string().nullish(),
    contacts__id: z.string(),
    contacts__name: z.string().nullish(),
    system_users__id: z.string().nullish(),
    system_users__name: z.string().nullish(),
    system_users__email: z.string().nullish(),
    contact_relationship_titles__id: z.string(),
    contact_relationship_titles__title_key: z.string().nullish(),
    contact_relationship_titles__name: z.string(),
    contact_relationship_titles__is_exclusive: z.boolean(),
    prog__is_current: z.boolean()
  })
  .strict()
export type AccountOwnerRecord = z.infer<typeof AccountOwnerRecordSchema>
export const AccountOwnerRecordFields = makeFieldKeys(AccountOwnerRecordSchema)
export const validateAccountOwnersList = (data: unknown): AccountOwnerRecord[] =>
  validateWithZodSchema(z.array(AccountOwnerRecordSchema), data, 'account owners') as AccountOwnerRecord[]

// A current holder as embedded by account_owners__current / the allocation grid.
export const AccountOwnerHolderSchema = z
  .object({
    contact_accounts__id: z.string(),
    contacts__id: z.string().nullish(),
    contacts__name: z.string().nullish(),
    system_users__id: z.string().nullish(),
    system_users__name: z.string().nullish()
  })
  .passthrough()
export type AccountOwnerHolder = z.infer<typeof AccountOwnerHolderSchema>

// ── LP Allocation grid ─────────────────────────────────────────────────────
export const LpAllocationRowSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string(),
    comms_lp_statuses__id: z.string().nullish(),
    comms_lp_statuses__label: z.string().nullish(),
    // Per-LP agent drafts override (PR D): null = status default.
    accounts__agent_drafts_enabled: z.boolean().nullish(),
    // Grid text for the toggle ('Default' | 'On' | 'Off'), the LP's funds
    // (Closed commitment or live prospect) as a label and as ids (PR E).
    prog__agent_drafts: z.string(),
    prog__funds: z.string(),
    prog__fund_ids: z.array(z.string()).nullish(),
    prog__commitment_total: z.number().nullish(),
    prog__commitment_currency: z.string().nullish(),
    prog__commitment_total_fmt: z.string().nullish(),
    prog__prospects_count: z.number(),
    // {role_key: [holder, ...]}
    prog__owners: z.record(z.string(), z.array(AccountOwnerHolderSchema)),
    prog__owner_user_ids: z.array(z.string()),
    prog__unassigned: z.boolean()
  })
  .strict()
export type LpAllocationRow = z.infer<typeof LpAllocationRowSchema>
export const LpAllocationRowFields = makeFieldKeys(LpAllocationRowSchema)
export const validateLpAllocationRows = (data: unknown): LpAllocationRow[] =>
  validateWithZodSchema(z.array(LpAllocationRowSchema), data, 'LP allocation rows') as LpAllocationRow[]

export const FilterOptionSchema = z.object({ value: z.string(), label: z.string() }).strict()
export const LpAllocationFilterOptionsSchema = z
  .object({
    system_users__id: z.array(FilterOptionSchema),
    comms_lp_statuses__id: z.array(FilterOptionSchema),
    funds__id: z.array(FilterOptionSchema),
    accounts__agent_drafts_enabled: z.array(FilterOptionSchema)
  })
  .strict()
export type LpAllocationFilterOptions = z.infer<typeof LpAllocationFilterOptionsSchema>
export const validateLpAllocationFilterOptions = (data: unknown): LpAllocationFilterOptions =>
  validateWithZodSchema(LpAllocationFilterOptionsSchema, data, 'LP allocation filter options') as LpAllocationFilterOptions

// ── Relationship facts ─────────────────────────────────────────────────────
export const RelationshipFactRecordSchema = z
  .object({
    relationship_facts__id: z.string(),
    relationship_facts__account_id: z.string(),
    relationship_facts__contact_id: z.string().nullish(),
    relationship_facts__prospect_id: z.string().nullish(),
    relationship_facts__fact_type_id: z.string(),
    relationship_facts__content: z.string(),
    relationship_facts__structured: z.record(z.string(), z.unknown()).nullish(),
    relationship_facts__source: z.string(),
    relationship_facts__agent_run_id: z.string().nullish(),
    relationship_facts__source_interaction_id: z.string().nullish(),
    relationship_facts__status: z.string(),
    relationship_facts__status_reason: z.string().nullish(),
    relationship_facts__reviewed_at: z.string().nullish(),
    relationship_facts__superseded_by_id: z.string().nullish(),
    relationship_facts__importance: z.number(),
    relationship_facts__last_confirmed_at: z.string(),
    relationship_facts__date_created: z.string(),
    relationship_facts__date_modified: z.string().nullish(),
    relationship_fact_types__type_key: z.string(),
    relationship_fact_types__label: z.string(),
    relationship_fact_types__is_personal: z.boolean(),
    relationship_fact_types__visible_in_drafts: z.boolean(),
    contacts__id: z.string().nullish(),
    contacts__name: z.string().nullish(),
    prospects__id: z.string().nullish(),
    prospects__name: z.string().nullish(),
    funds__name: z.string().nullish(),
    interactions__id: z.string().nullish(),
    interactions__subject: z.string().nullish(),
    interactions__datetime: z.string().nullish(),
    internal_agent_types__agent_type_key: z.string().nullish(),
    internal_agent_types__name: z.string().nullish(),
    prog__created_by_name: z.string().nullish(),
    prog__reviewed_by_name: z.string().nullish()
  })
  .strict()
export type RelationshipFactRecord = z.infer<typeof RelationshipFactRecordSchema>
export const RelationshipFactRecordFields = makeFieldKeys(RelationshipFactRecordSchema)
export const validateRelationshipFactsList = (data: unknown): RelationshipFactRecord[] =>
  validateWithZodSchema(z.array(RelationshipFactRecordSchema), data, 'relationship facts') as RelationshipFactRecord[]

// ── LP fund exposure ───────────────────────────────────────────────────────
export const AccountFundExposureRowSchema = z
  .object({
    target_id: z.string().nullish(),
    kind: z.string(),
    funds__name: z.string().nullish(),
    fund_ids: z.array(z.string()).nullish(),
    fund_commitment_statuses__label: z.string().nullish(),
    commitment: z.number().nullish(),
    commitment_fmt: z.string().nullish(),
    currency_code: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    date_closed: z.string().nullish(),
    date_committed: z.string().nullish(),
    fund_shares: z.record(z.string(), z.number()).nullish(),
    fund_share: z.number().nullish(),
    ownership: z.number().nullish(),
    called_to_date: z.number().nullish(),
    called_to_date_fmt: z.string().nullish(),
    distributions_to_date: z.number().nullish(),
    distributions_to_date_fmt: z.string().nullish(),
    fund_total_value: z.number().nullish(),
    fund_fmv: z.number().nullish(),
    see_through_nav: z.number().nullish(),
    see_through_nav_fmt: z.string().nullish(),
    see_through_fmv: z.number().nullish(),
    exposure_weight: z.number().nullish(),
    exposure_weight_fmt: z.string().nullish()
  })
  .strict()
export const AccountFundExposureSchema = z
  .object({
    account: z.object({ id: z.string(), name: z.string() }).nullish(),
    as_of: z.string().nullish(),
    rows: z.array(AccountFundExposureRowSchema),
    totals: z
      .object({
        see_through_nav: z.number().nullish(),
        called_to_date: z.number().nullish(),
        closed_targets: z.number().nullish(),
        commitment_by_currency: z.array(z.object({ currency_code: z.string().nullish(), amount: z.number().nullish() }).strict()).nullish()
      })
      .passthrough()
  })
  .strict()
export type AccountFundExposure = z.infer<typeof AccountFundExposureSchema>
export type AccountFundExposureRow = z.infer<typeof AccountFundExposureRowSchema>
export const validateAccountFundExposure = (data: unknown): AccountFundExposure =>
  validateWithZodSchema(AccountFundExposureSchema, data, 'account fund exposure') as AccountFundExposure

// ── Configuration lookups (admin panels) ───────────────────────────────────
// Phase 8: the owner roles are the titles of the account_ownership
// contact-relationship type.
export const AccountOwnerRoleAdminRowSchema = z
  .object({
    contact_relationship_titles__id: z.string(),
    contact_relationship_titles__title_key: z.string().nullish(),
    contact_relationship_titles__name: z.string(),
    contact_relationship_titles__description: z.string().nullish(),
    contact_relationship_titles__is_exclusive: z.boolean(),
    contact_relationship_titles__sort_order: z.number().nullish(),
    contact_relationship_titles__date_modified: z.string().nullish(),
    prog__usage_count: z.number()
  })
  .strict()
export type AccountOwnerRoleAdminRow = z.infer<typeof AccountOwnerRoleAdminRowSchema>
export const validateAccountOwnerRoleAdminRows = (data: unknown): AccountOwnerRoleAdminRow[] =>
  validateWithZodSchema(z.array(AccountOwnerRoleAdminRowSchema), data, 'account owner roles') as AccountOwnerRoleAdminRow[]

export const RelationshipFactTypeAdminRowSchema = z
  .object({
    relationship_fact_types__id: z.string(),
    relationship_fact_types__type_key: z.string(),
    relationship_fact_types__label: z.string(),
    relationship_fact_types__description: z.string().nullish(),
    relationship_fact_types__guidance: z.string().nullish(),
    relationship_fact_types__is_personal: z.boolean(),
    relationship_fact_types__visible_in_drafts: z.boolean(),
    relationship_fact_types__sort_order: z.number(),
    relationship_fact_types__enabled: z.boolean(),
    relationship_fact_types__date_modified: z.string().nullish(),
    prog__usage_count: z.number()
  })
  .strict()
export type RelationshipFactTypeAdminRow = z.infer<typeof RelationshipFactTypeAdminRowSchema>
export const validateRelationshipFactTypeAdminRows = (data: unknown): RelationshipFactTypeAdminRow[] =>
  validateWithZodSchema(z.array(RelationshipFactTypeAdminRowSchema), data, 'relationship fact types') as RelationshipFactTypeAdminRow[]
